import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Copy, Clock, MapPin } from "lucide-react";

export default function Contact() {
  const [info, setInfo] = useState({
    email: "itsfootballzonecouk@gmail.com",
    name: "The Football Zone",
    reply_hours: "Mon–Fri, 9am–6pm UK time",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/contact");
        setInfo(r.data);
      } catch {
        // fall back to defaults quietly
      }
    })();
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.email);
      toast.success("Email address copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
      <div className="font-mono-retro text-[11px] tracking-[0.4em] text-[#C8A96A] uppercase">Get In Touch</div>
      <h1 className="font-display text-5xl md:text-6xl text-[#F2EBDD] mt-2 leading-tight">Contact Us</h1>
      <p className="text-[#A8A39D] mt-4 max-w-xl leading-relaxed">
        Question about a raffle, a jersey, or a bank transfer that hasn&apos;t landed yet?
        Drop us a line and we&apos;ll get back to you within a working day.
      </p>

      <div className="perforated my-10" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Email card */}
        <div className="md:col-span-2 border-2 border-[#332D2B] bg-[#1C1816] p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 border border-[#C8A96A] flex items-center justify-center shrink-0">
              <Mail className="text-[#C8A96A]" size={20} />
            </div>
            <div className="flex-1">
              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">Email us</div>
              <a
                href={`mailto:${info.email}`}
                data-testid="contact-email-link"
                className="font-display text-3xl md:text-4xl text-[#F2EBDD] hover:text-[#C8A96A] transition-colors break-all block mt-1"
              >
                {info.email}
              </a>
              <p className="text-[#A8A39D] text-sm mt-3 leading-relaxed">
                For order queries include your <span className="text-[#C8A96A] font-mono-retro">FZ-XXXXXX</span> reference so we can find you fast.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <a
                  data-testid="contact-mailto-btn"
                  href={`mailto:${info.email}?subject=football.zone%20enquiry`}
                  className="btn-primary"
                >
                  <Mail size={16} /> Open Mail App
                </a>
                <button
                  data-testid="contact-copy-btn"
                  onClick={copy}
                  className="btn-secondary"
                >
                  <Copy size={16} /> Copy Address
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="border border-[#332D2B] bg-[#0F0C0B] p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="text-[#C8A96A]" size={16} />
            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase">Reply Hours</div>
          </div>
          <div className="text-[#F2EBDD]" data-testid="contact-reply-hours">{info.reply_hours}</div>
          <div className="perforated my-5" />
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="text-[#C8A96A]" size={16} />
            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase">Ships From</div>
          </div>
          <div className="text-[#F2EBDD]">United Kingdom</div>
          <div className="text-[#756F6B] text-xs mt-1">Worldwide delivery in 10–17 days</div>
        </div>
      </div>

      {/* FAQ-lite */}
      <div className="mt-14">
        <div className="font-mono-retro text-[11px] tracking-[0.4em] text-[#C8A96A] uppercase">Before You Write</div>
        <h2 className="font-display text-3xl text-[#F2EBDD] mt-1 mb-6">Quick answers</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="border border-[#332D2B] bg-[#1C1816] p-5">
            <div className="font-display text-lg text-[#F2EBDD]">How long does shipping take?</div>
            <p className="text-[#A8A39D] text-sm mt-2 leading-relaxed">10–17 days from confirmed payment. You&apos;ll get an email when it leaves.</p>
          </div>
          <div className="border border-[#332D2B] bg-[#1C1816] p-5">
            <div className="font-display text-lg text-[#F2EBDD]">I sent the transfer — now what?</div>
            <p className="text-[#A8A39D] text-sm mt-2 leading-relaxed">Once it clears in our account, we mark your order Paid and email you.</p>
          </div>
          <div className="border border-[#332D2B] bg-[#1C1816] p-5">
            <div className="font-display text-lg text-[#F2EBDD]">Can I change my custom print?</div>
            <p className="text-[#A8A39D] text-sm mt-2 leading-relaxed">Yes, as long as we haven&apos;t started production — email us your reference within 24 hours.</p>
          </div>
          <div className="border border-[#332D2B] bg-[#1C1816] p-5">
            <div className="font-display text-lg text-[#F2EBDD]">Lost your order page?</div>
            <p className="text-[#A8A39D] text-sm mt-2 leading-relaxed">Send us your name and email — we&apos;ll dig out your FZ- reference for you.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
